import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { auth, db } from '../../firebase/config';
import { handleFirebaseError } from '../../utils/errorHandler';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { NativeSelect } from '../ui/select-native';
import SEO from '../SEO';
import { countries, getCountry } from '../../lib/countries';

const UserSignup = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [steamLink, setSteamLink] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) return setError('Username is required.');
    if (!email.trim()) return setError('Email is required.');
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (!steamLink.trim()) return setError('Steam profile link is required.');
    if (!getCountry(location)) return setError('Country is required.');

    setLoading(true);
    try {
      const dupe = await getDocs(
        query(collection(db, 'userProfiles'), where('username', '==', username.trim()))
      );
      if (!dupe.empty) throw new Error('Username is already taken.');

      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const user = credential.user;
      const country = getCountry(location);

      await setDoc(doc(db, 'userProfiles', user.uid), {
        username: username.trim(),
        email: email.trim(),
        hasEmail: true,
        location,
        countryName: country?.name || '',
        steamLink: steamLink.trim(),
        createdAt: new Date().toISOString(),
        role: 'user',
      });

      try {
        await sendEmailVerification(user);
      } catch (err) {
        console.warn('sendEmailVerification failed:', err);
      }

      toast.success('Account created.', {
        description: 'Check your inbox to verify your email address.',
      });
      navigate('/');
    } catch (err) {
      setError(handleFirebaseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-col px-4 py-12 sm:px-6">
      <SEO title="Sign Up" canonicalPath="/signup" noindex />
      <div className="mb-8 flex flex-col gap-2">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-primary" />
          Join
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Create your account</h1>
        <p className="text-sm text-muted-foreground">
          Already have one?{' '}
          <Link to="/login" className="text-foreground underline underline-offset-4">
            Log in
          </Link>
          .
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-5 rounded-lg border border-border/60 bg-card p-6 shadow-sm"
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="username">Username *</Label>
          <Input
            id="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            placeholder="you@example.com"
          />
          <p className="text-xs text-muted-foreground">
            We'll send a verification link to this address.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="password">Password *</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">At least 6 characters.</p>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="steam">Steam profile URL *</Label>
          <Input
            id="steam"
            value={steamLink}
            onChange={(e) => setSteamLink(e.target.value)}
            placeholder="https://steamcommunity.com/id/yourprofile"
            disabled={loading}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="country">Country *</Label>
          <NativeSelect
            id="country"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={loading}
            required
          >
            <option value="">Select a country…</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </NativeSelect>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <Button type="submit" disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UserPlus className="h-4 w-4" />
          )}
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </div>
  );
};

export default UserSignup;
